import { RealTimeAPIInterceptor, IRealTimeAPIInterceptorConfig } from './network-interceptor/RealTimeAPIInterceptor';
import { RealTimeErrorReporter, IRealTimeErrorReporterConfig } from './error-detector/RealTimeErrorReporter';
import { APIStateMapper, IAPIStateMapperConfig } from './state-mapper/APIStateMapper';
import { TimeTravelEngine } from './time-travel-engine';
import { generateUniqueId } from './utils';

export interface IDebugTimeMachineConfig {
  // WebSocket 설정
  websocket?: {
    url: string;
    reconnectInterval?: number;
    maxReconnectAttempts?: number;
    enableHeartbeat?: boolean;
    heartbeatInterval?: number;
  };
  
  // 네트워크 인터셉션 설정
  networkInterception?: Partial<IRealTimeAPIInterceptorConfig>;
  
  // 에러 리포팅 설정
  errorReporting?: Partial<IRealTimeErrorReporterConfig>;
  
  // 상태 매핑 설정
  stateMapping?: Partial<IAPIStateMapperConfig>;
  
  // 일반 설정
  enableNetworkInterception?: boolean;
  enableErrorReporting?: boolean;
  enableStateMapping?: boolean;
  autoStart?: boolean;
  enableLogging?: boolean;
  sessionId?: string;
}

export interface IDebugTimeMachineStats {
  uptime: number;
  sessionId: string;
  isActive: boolean;
  
  // 네트워크 통계
  network: {
    totalRequests: number;
    totalResponses: number;
    totalErrors: number;
    webSocketConnected: boolean;
  };
  
  // 에러 통계
  errors: {
    totalErrors: number;
    sentErrors: number;
    bufferedErrors: number;
  };
  
  // 상태 매핑 통계
  stateMapping: {
    totalMappings: number;
    averageConfidence: number;
    learnedPatterns: number;
  };
}

/**
 * Debug Time Machine 통합 엔진
 * 
 * 모든 핵심 기능들을 통합하고 관리하는 메인 클래스입니다.
 */
export class DebugTimeMachineEngine {
  private _config: Required<IDebugTimeMachineConfig>;
  private _isActive: boolean = false;
  private _sessionId: string;
  private _startTime: number = 0;
  
  // 핵심 컴포넌트들
  private _apiInterceptor: RealTimeAPIInterceptor | null = null;
  private _errorReporter: RealTimeErrorReporter | null = null;
  private _stateMapper: APIStateMapper | null = null;
  private _timeTravelEngine: TimeTravelEngine | null = null;

  constructor(config: IDebugTimeMachineConfig = {}) {
    this._sessionId = config.sessionId || this._generateSessionId();
    
    const defaultWebSocketConfig = {
      url: 'ws://localhost:8080/ws',
      reconnectInterval: 3000,
      maxReconnectAttempts: 10,
      enableHeartbeat: true,
      heartbeatInterval: 30000,
    };

    this._config = {
      websocket: { ...defaultWebSocketConfig, ...config.websocket },
      networkInterception: config.networkInterception || {},
      errorReporting: config.errorReporting || {},
      stateMapping: config.stateMapping || {},
      enableNetworkInterception: config.enableNetworkInterception ?? true,
      enableErrorReporting: config.enableErrorReporting ?? true,
      enableStateMapping: config.enableStateMapping ?? true,
      autoStart: config.autoStart ?? false,
      enableLogging: config.enableLogging ?? true,
      sessionId: this._sessionId,
    };

    this._log('DebugTimeMachineEngine initialized', {
      sessionId: this._sessionId,
      config: this._config,
    });

    // 컴포넌트들 초기화
    this._initializeComponents();

    // 자동 시작
    if (this._config.autoStart) {
      this.start().catch(error => {
        this._log('Auto-start failed:', error);
      });
    }
  }

  /**
   * Debug Time Machine 시작
   */
  public async start(): Promise<void> {
    if (this._isActive) {
      this._log('Already active');
      return;
    }

    this._log('Starting Debug Time Machine...');
    this._startTime = Date.now();

    try {
      const startPromises: Promise<void>[] = [];

      // API 인터셉션 시작
      if (this._config.enableNetworkInterception && this._apiInterceptor) {
        startPromises.push(this._apiInterceptor.start());
      }

      // 에러 리포팅 시작
      if (this._config.enableErrorReporting && this._errorReporter) {
        startPromises.push(this._errorReporter.start());
      }

      // 상태 매핑 시작
      if (this._config.enableStateMapping && this._stateMapper) {
        this._stateMapper.start();
      }

      // 시간여행 엔진 시작
      if (this._timeTravelEngine) {
        this._timeTravelEngine.start();
      }

      // 모든 비동기 시작 작업 완료 대기
      await Promise.allSettled(startPromises);

      this._isActive = true;
      this._log('Debug Time Machine started successfully');

      // 시작 이벤트 전송
      this._sendEngineEvent('started');

    } catch (error) {
      this._log('Failed to start Debug Time Machine:', error);
      throw error;
    }
  }

  /**
   * Debug Time Machine 중지
   */
  public async stop(): Promise<void> {
    if (!this._isActive) {
      return;
    }

    this._log('Stopping Debug Time Machine...');

    try {
      const stopPromises: Promise<void>[] = [];

      // 시간여행 엔진 중지
      if (this._timeTravelEngine) {
        this._timeTravelEngine.stop();
      }

      // 상태 매핑 중지
      if (this._stateMapper) {
        this._stateMapper.stop();
      }

      // 에러 리포팅 중지
      if (this._errorReporter) {
        stopPromises.push(this._errorReporter.stop());
      }

      // API 인터셉션 중지
      if (this._apiInterceptor) {
        stopPromises.push(this._apiInterceptor.stop());
      }

      // 모든 비동기 중지 작업 완료 대기
      await Promise.allSettled(stopPromises);

      // 종료 이벤트 전송
      this._sendEngineEvent('stopped');

      this._isActive = false;
      this._log('Debug Time Machine stopped');

    } catch (error) {
      this._log('Error while stopping Debug Time Machine:', error);
      throw error;
    }
  }

  /**
   * WebSocket 수동 연결
   */
  public async connectWebSocket(): Promise<void> {
    const promises: Promise<void>[] = [];

    if (this._apiInterceptor) {
      promises.push(this._apiInterceptor.connectWebSocket());
    }

    if (this._errorReporter) {
      promises.push(this._errorReporter.connectWebSocket());
    }

    await Promise.allSettled(promises);
    this._log('WebSocket connections established');
  }

  /**
   * WebSocket 연결 해제
   */
  public disconnectWebSocket(): void {
    if (this._apiInterceptor) {
      this._apiInterceptor.disconnectWebSocket();
    }

    if (this._errorReporter) {
      this._errorReporter.disconnectWebSocket();
    }

    this._log('WebSocket connections closed');
  }

  /**
   * 에러 수동 리포트
   */
  public reportError(error: Error, context?: Record<string, unknown>): void {
    if (this._errorReporter) {
      this._errorReporter.reportError(error, context);
    }
  }

  /**
   * 브레드크럼 추가
   */
  public addBreadcrumb(breadcrumb: {
    type: 'navigation' | 'user_action' | 'http' | 'console' | 'state_change';
    message: string;
    data?: Record<string, unknown>;
    level?: 'info' | 'warning' | 'error';
  }): void {
    if (this._errorReporter) {
      this._errorReporter.addBreadcrumb(breadcrumb);
    }
  }

  /**
   * 상태 변경 수동 추가
   */
  public addStateChange(stateChange: {
    statePath: string;
    oldValue: unknown;
    newValue: unknown;
    source?: 'api' | 'user' | 'system' | 'unknown';
  }): void {
    if (this._stateMapper) {
      this._stateMapper.addStateChange({
        ...stateChange,
        source: stateChange.source || 'unknown',
      });
    }
  }

  /**
   * 시간여행 - 특정 시점으로 이동
   */
  public travelToTime(timestamp: number): void {
    if (this._timeTravelEngine) {
      this._timeTravelEngine.travelToTime(timestamp);
    }
  }

  /**
   * 시간여행 - 이전 이벤트로 이동
   */
  public travelToPreviousEvent(): void {
    if (this._timeTravelEngine) {
      this._timeTravelEngine.travelToPreviousEvent();
    }
  }

  /**
   * 시간여행 - 다음 이벤트로 이동
   */
  public travelToNextEvent(): void {
    if (this._timeTravelEngine) {
      this._timeTravelEngine.travelToNextEvent();
    }
  }

  /**
   * 현재 상태 스냅샷 생성
   */
  public createSnapshot(): void {
    if (this._timeTravelEngine) {
      this._timeTravelEngine.createSnapshot();
    }
  }

  /**
   * 히스토리 초기화
   */
  public clearHistory(): void {
    if (this._apiInterceptor) {
      this._apiInterceptor.clearHistory();
    }
    
    if (this._timeTravelEngine) {
      this._timeTravelEngine.clearHistory();
    }

    this._log('History cleared');
  }

  /**
   * 통계 정보 가져오기
   */
  public getStats(): IDebugTimeMachineStats {
    const uptime = Date.now() - this._startTime;

    return {
      uptime,
      sessionId: this._sessionId,
      isActive: this._isActive,
      network: this._getNetworkStats(),
      errors: this._getErrorStats(),
      stateMapping: this._getStateMappingStats(),
    };
  }

  /**
   * 상세 상태 정보
   */
  public getDetailedState() {
    return {
      engine: {
        isActive: this._isActive,
        sessionId: this._sessionId,
        uptime: Date.now() - this._startTime,
        config: { ...this._config },
      },
      apiInterceptor: this._apiInterceptor?.getState(),
      errorReporter: this._errorReporter?.getState(),
      stateMapper: this._stateMapper?.getStats(),
      timeTravelEngine: this._timeTravelEngine?.getState(),
    };
  }

  /**
   * API 매핑 정보 가져오기
   */
  public getAPIMappings() {
    return this._stateMapper?.getMappings() || [];
  }

  /**
   * 네트워크 요청 히스토리
   */
  public getNetworkHistory() {
    return {
      requests: this._apiInterceptor?.getRequestHistory() || [],
      responses: this._apiInterceptor?.getResponseHistory() || [],
    };
  }

  /**
   * 상태 변경 히스토리
   */
  public getStateChangeHistory() {
    return this._stateMapper?.getStateChangeHistory() || [];
  }

  /**
   * 학습된 패턴들
   */
  public getLearnedPatterns() {
    return this._stateMapper?.getLearnedPatterns() || new Map();
  }

  /**
   * 컴포넌트들 초기화
   */
  private _initializeComponents(): void {
    // API 인터셉터 초기화
    if (this._config.enableNetworkInterception) {
      this._apiInterceptor = new RealTimeAPIInterceptor({
        ...this._config.networkInterception,
        websocket: this._config.websocket,
        enableLogging: this._config.enableLogging,
      });
    }

    // 에러 리포터 초기화
    if (this._config.enableErrorReporting) {
      this._errorReporter = new RealTimeErrorReporter({
        ...this._config.errorReporting,
        websocket: this._config.websocket,
        enableLogging: this._config.enableLogging,
      });
    }

    // 상태 매퍼 초기화
    if (this._config.enableStateMapping) {
      this._stateMapper = new APIStateMapper({
        ...this._config.stateMapping,
        onMappingCreated: (mapping) => {
          this._log('API-State mapping created:', mapping);
        },
        onStateChange: (stateChange) => {
          if (this._config.enableLogging) {
            this._log('State change detected:', stateChange);
          }
        },
      });

      // API 인터셉터와 상태 매퍼 연동
      if (this._apiInterceptor) {
        this._setupAPIStateMapping();
      }
    }

    // 시간여행 엔진 초기화
    this._timeTravelEngine = new TimeTravelEngine({
      enableLogging: this._config.enableLogging,
    });
  }

  /**
   * API-상태 매핑 연동 설정
   */
  private _setupAPIStateMapping(): void {
    if (!this._apiInterceptor || !this._stateMapper) {
      return;
    }

    // 기존 네트워크 인터셉터 설정을 확장하여 상태 매핑 연동
    const originalNetworkConfig = this._config.networkInterception;
    const originalOnRequest = originalNetworkConfig.onRequest;
    const originalOnResponse = originalNetworkConfig.onResponse;

    // 요청 추적
    this._config.networkInterception.onRequest = (request) => {
      this._stateMapper?.trackAPIRequest(request);
      originalOnRequest?.(request);
    };

    // 응답 추적
    this._config.networkInterception.onResponse = (response) => {
      this._stateMapper?.trackAPIResponse(response);
      originalOnResponse?.(response);
    };
  }

  /**
   * 네트워크 통계 수집
   */
  private _getNetworkStats() {
    const stats = this._apiInterceptor?.getStats();
    return {
      totalRequests: stats?.networkMetrics.totalRequests || 0,
      totalResponses: stats?.networkMetrics.completedRequests || 0,
      totalErrors: stats?.networkMetrics.errorCount || 0,
      webSocketConnected: stats?.webSocketConnected || false,
    };
  }

  /**
   * 에러 통계 수집
   */
  private _getErrorStats() {
    const stats = this._errorReporter?.getStats();
    return {
      totalErrors: stats?.totalErrors || 0,
      sentErrors: stats?.sentErrors || 0,
      bufferedErrors: stats?.bufferedErrors || 0,
    };
  }

  /**
   * 상태 매핑 통계 수집
   */
  private _getStateMappingStats() {
    const stats = this._stateMapper?.getStats();
    return {
      totalMappings: stats?.totalMappings || 0,
      averageConfidence: stats?.averageConfidence || 0,
      learnedPatterns: stats?.learnedPatterns || 0,
    };
  }

  /**
   * 엔진 이벤트 전송
   */
  private _sendEngineEvent(eventType: 'started' | 'stopped' | 'error'): void {
    // 이벤트를 브레드크럼으로 추가
    this.addBreadcrumb({
      type: 'console',
      message: `Debug Time Machine ${eventType}`,
      level: eventType === 'error' ? 'error' : 'info',
      data: {
        sessionId: this._sessionId,
        timestamp: Date.now(),
        stats: this.getStats(),
      },
    });
  }

  /**
   * 세션 ID 생성
   */
  private _generateSessionId(): string {
    return `dtm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 로깅 헬퍼
   */
  private _log(message: string, data?: unknown): void {
    if (!this._config.enableLogging) {
      return;
    }

    const timestamp = new Date().toISOString();
    console.log(`[DebugTimeMachineEngine] ${timestamp} ${message}`, data || '');
  }

  /**
   * 정리
   */
  public async destroy(): Promise<void> {
    this._log('Destroying Debug Time Machine Engine...');
    
    await this.stop();
    
    // 모든 컴포넌트들 정리
    if (this._apiInterceptor) {
      await this._apiInterceptor.destroy();
      this._apiInterceptor = null;
    }
    
    if (this._errorReporter) {
      await this._errorReporter.destroy();
      this._errorReporter = null;
    }
    
    if (this._stateMapper) {
      this._stateMapper.destroy();
      this._stateMapper = null;
    }
    
    if (this._timeTravelEngine) {
      this._timeTravelEngine.destroy();
      this._timeTravelEngine = null;
    }
    
    this._log('Debug Time Machine Engine destroyed');
  }

  /**
   * 현재 활성 상태
   */
  public get isActive(): boolean {
    return this._isActive;
  }

  /**
   * 세션 ID
   */
  public get sessionId(): string {
    return this._sessionId;
  }

  /**
   * 업타임 (밀리초)
   */
  public get uptime(): number {
    return this._startTime > 0 ? Date.now() - this._startTime : 0;
  }
}

// 전역 인스턴스
let globalEngine: DebugTimeMachineEngine | null = null;

/**
 * 전역 Debug Time Machine Engine 가져오기/생성
 */
export function getGlobalDebugTimeMachine(config?: IDebugTimeMachineConfig): DebugTimeMachineEngine {
  if (!globalEngine) {
    globalEngine = new DebugTimeMachineEngine(config);
  }
  return globalEngine;
}

/**
 * 전역 Debug Time Machine Engine 정리
 */
export async function destroyGlobalDebugTimeMachine(): Promise<void> {
  if (globalEngine) {
    await globalEngine.destroy();
    globalEngine = null;
  }
}

/**
 * 간편한 시작 함수
 */
export async function startDebugTimeMachine(config?: IDebugTimeMachineConfig): Promise<DebugTimeMachineEngine> {
  const engine = getGlobalDebugTimeMachine(config);
  await engine.start();
  return engine;
}

/**
 * 간편한 중지 함수
 */
export async function stopDebugTimeMachine(): Promise<void> {
  if (globalEngine) {
    await globalEngine.stop();
  }
}
